import { Link } from "react-router-dom";

export default function HowToCalculateTip() {
    return (
        <>
            <p>
                Tipping has gotten more complicated in the last few years. Pre-selected tip prompts on tablet screens now
                appear at coffee shops, takeout counters, and self-serve kiosks that didn't used to have tip jars. Meanwhile,
                restaurant tipping customs have shifted upward. Knowing how to calculate what you owe — and what's reasonable —
                takes a minute of clarity.
            </p>

            <h2>The Basic Math</h2>
            <p>
                The tip amount is straightforward: multiply the pre-tax bill by the tip percentage you want to leave.
            </p>
            <p>
                Tip = Bill × (Tip % ÷ 100)
            </p>
            <p>
                On a $64 check:
            </p>
            <p>
                15% tip: $64 × 0.15 = $9.60<br />
                18% tip: $64 × 0.18 = $11.52<br />
                20% tip: $64 × 0.20 = $12.80<br />
                25% tip: $64 × 0.25 = $16.00
            </p>
            <p>
                Total bill = original check + tip. At 20%, you'd pay $64 + $12.80 = $76.80.
            </p>
            <p>
                If you're splitting with a group, divide the total bill (including tip) by the number of people. Four people
                splitting a $76.80 check each pay $19.20.
            </p>
            <p>
                For quick calculations without doing the math by hand, use the{" "}
                <Link to="/calculator/tip">tip calculator</Link> — it handles any bill, tip percentage, and split in seconds.
            </p>

            <h2>The Quick Mental Math Tricks</h2>
            <p>
                You don't always have your phone handy. A few fast methods:
            </p>
            <p>
                <strong>10% method:</strong> Move the decimal point one place left to find 10% of the bill. Double it for
                20%, or take half of it and add to 10% for 15%.
            </p>
            <p>
                On a $58 check:<br />
                10% = $5.80<br />
                20% = $11.60 (double the 10%)<br />
                15% = $8.70 ($5.80 + half of $5.80 = $5.80 + $2.90)
            </p>
            <p>
                <strong>Double the tax method:</strong> In many states, sales tax is between 8% and 10%. Doubling the tax
                amount on your receipt gets you close to a 16%–20% tip without any mental math. It's not precise, but it's
                fast and gets you in the right range.
            </p>

            <h2>What about pre-set tip prompts on tablets?</h2>
            <p>
                Those 18%/22%/25% buttons on tablet payment screens are often calculated on the post-tax total, not the
                pre-tax total — which inflates the actual tip a few percentage points. If you want exactly 20% on the
                food, hit "custom" and enter 20% of the subtotal.
            </p>
            <p>
                For takeout and counter service, tipping has become normalized but isn't required. $1–$2 for a coffee
                order or 10% on a takeout meal is reasonable; full table-service tips (18%–22%) at a counter aren't.
            </p>

            <h2>Sit-down restaurants: what's reasonable in 2026</h2>
            <p>
                Standard service: 18%–20% of the pre-tax bill. Excellent service: 22%–25%. Mediocre service: 15%, but
                consider speaking to a manager if the issue was real. Tipping below 15% is best reserved for genuinely
                bad service — and in that case, tipping zero and writing a calm note is more respectful than punishing
                a server with $2 on a $80 bill.
            </p>

            <p>
                Skip the mental math entirely with our <Link to="/calculator/tip">tip calculator</Link> — bills,
                percentages, and splits handled in a couple of taps.
            </p>
        </>
    );
}